import purify from "dompurify";

import { useTranslate } from "@similarweb/i18n-provider";
import { Check } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Modal } from "@similarweb/ui-components";
import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import { defaultDiscountPopupTheme } from "app/components/discountCampaign/DiscountBanner/utils/getDiscountTheme";
import { CountdownTimer } from "app/components/timers/CountdownTimer/CountdownTimer";
import { AssetsService } from "app/services/AssetsService";

import {
    ContentWrapper,
    CloseIconButton,
    Title,
    LeftContent,
    RightContent,
    CtaButton,
    CloseButton,
    CampaignModalImage,
    PercentageText,
} from "./StyledCampaignPromoModal";
import type { ICampaignPromoModalProps } from "./types";

export const CampaignPromoModal = ({
    theme,
    content,
    features,
    discountEndTime,
    isOpen,
    onClose,
    onCtaClick,
}: ICampaignPromoModalProps) => {
    const { translate } = useTranslate();

    const isWithFeatures = theme.layout === "withFeatures";

    const timerTheme = {
        ...defaultDiscountPopupTheme,
        timeBoxWrapperBorderColor: theme.timerBoxBorderColor ?? defaultDiscountPopupTheme.timeBoxWrapperBorderColor,
        timeBoxWrapperBackground: theme.timerBoxBackground ?? defaultDiscountPopupTheme.timeBoxWrapperBackground,
        timeValueColor: theme.timerBoxTextColor ?? defaultDiscountPopupTheme.timeValueColor,
        timeLabelColor: theme.timerBoxTextColor ?? defaultDiscountPopupTheme.timeLabelColor,
        withLabel: true,
    };

    return (
        <Modal
            data-automation="campaign-promo-modal"
            customStyles={{
                content: {
                    width: theme.modalSize.width,
                    height: theme.modalSize.height,
                    backgroundColor: colorsPalettes.black[0],
                    border: 0,
                },
            }}
            isOpen={isOpen}
        >
            <ContentWrapper $theme={theme}>
                {!isWithFeatures ? (
                    <CloseIconButton
                        data-automation="campaign-promo-modal-close"
                        onClick={onClose}
                        iconSize="xs"
                        type="flat"
                        iconName="close"
                        iconColor={colorsPalettes.carbon[0]}
                    />
                ) : null}

                <LeftContent $theme={theme}>
                    {content.title ? (
                        <Title
                            $theme={theme}
                            dangerouslySetInnerHTML={{
                                __html: purify.sanitize(translate(content.title)),
                            }}
                        />
                    ) : null}
                    <Stack
                        gap="20px"
                        alignItems="center"
                    >
                        <Stack
                            gap="5px"
                            alignItems="center"
                        >
                            {content.text ? (
                                <Text
                                    textAlign="center"
                                    fontVariant="h5"
                                    color={colorsPalettes.bluegrey[100]}
                                    dangerouslySetInnerHTML={{
                                        __html: purify.sanitize(translate(content.text)),
                                    }}
                                />
                            ) : null}
                            {content.imageSrc ? (
                                <CampaignModalImage
                                    $size={content.imageSize}
                                    src={AssetsService.assetUrl(`images/${content.imageSrc}`)}
                                    alt="Campaign deals"
                                />
                            ) : null}
                            {content.percentage != null ? (
                                <PercentageText
                                    $theme={theme}
                                    dangerouslySetInnerHTML={{
                                        __html: purify.sanitize(
                                            translate(content.percentageTranslationKey ?? "discount.cta.modal.Xoff", {
                                                percentage: content.percentage,
                                            }),
                                        ),
                                    }}
                                />
                            ) : null}
                            {content.subText ? (
                                <Text
                                    textAlign="center"
                                    fontVariant="h5"
                                    color={colorsPalettes.bluegrey[100]}
                                    dangerouslySetInnerHTML={{
                                        __html: purify.sanitize(
                                            translate(content.subText, content.subTextParams ?? {}),
                                        ),
                                    }}
                                />
                            ) : null}
                        </Stack>
                        {content.discountCode ? (
                            <Stack gap="5px">
                                {content.getDiscountText ? (
                                    <Text
                                        textAlign="center"
                                        fontVariant="h5"
                                        color={colorsPalettes.bluegrey[100]}
                                        dangerouslySetInnerHTML={{
                                            __html: purify.sanitize(translate(content.getDiscountText)),
                                        }}
                                    />
                                ) : null}
                                <Text
                                    textAlign="center"
                                    fontVariant="h5"
                                    color={colorsPalettes.bluegrey[100]}
                                    dangerouslySetInnerHTML={{
                                        __html: purify.sanitize(
                                            translate("promo.use.code.bold", { code: content.discountCode }),
                                        ),
                                    }}
                                />
                            </Stack>
                        ) : null}
                    </Stack>
                    <Stack
                        alignItems="center"
                        justifyContent="center"
                        gap="16px"
                    >
                        {!isWithFeatures ? (
                            <>
                                {!content.isTimerHidden ? (
                                    <CountdownTimer
                                        discountEndTime={discountEndTime}
                                        theme={timerTheme}
                                    />
                                ) : null}
                                <CtaButton
                                    data-automation="campaign-promo-modal-cta"
                                    $color={theme.ctaButtonColor}
                                    onClick={onCtaClick}
                                >
                                    {translate(content.buttonText)}
                                </CtaButton>
                            </>
                        ) : null}
                    </Stack>
                </LeftContent>

                {isWithFeatures && features ? (
                    <RightContent $theme={theme}>
                        <Stack justifyContent="start">
                            <Stack>
                                <Text
                                    textAlign="left"
                                    fontVariant="subtitle2"
                                    color={colorsPalettes.carbon[25]}
                                    dangerouslySetInnerHTML={{
                                        __html: purify.sanitize(translate(features.title)),
                                    }}
                                />
                            </Stack>
                            <Stack
                                gap="8px"
                                margin="12px 0 24px"
                            >
                                {features.items.map((item, i) => (
                                    <Flex
                                        key={i}
                                        alignItems="center"
                                        gap="5px"
                                    >
                                        <Check
                                            size="xs"
                                            color={colorsPalettes.carbon[25]}
                                        />
                                        <Text
                                            textAlign="left"
                                            fontVariant="body2"
                                            color={colorsPalettes.carbon[25]}
                                            dangerouslySetInnerHTML={{
                                                __html: purify.sanitize(translate(item)),
                                            }}
                                        />
                                    </Flex>
                                ))}
                            </Stack>
                            {!content.isTimerHidden ? (
                                <CountdownTimer
                                    discountEndTime={discountEndTime}
                                    theme={timerTheme}
                                />
                            ) : null}
                        </Stack>

                        <Flex
                            gap="8px"
                            flexDirection="column"
                            alignItems="center"
                            width="100%"
                        >
                            <CtaButton
                                data-automation="campaign-promo-modal-cta"
                                $fullWidth
                                $color={theme.ctaButtonColor}
                                onClick={onCtaClick}
                            >
                                {translate(content.buttonText)}
                            </CtaButton>
                            <CloseButton
                                data-automation="campaign-promo-modal-close-text"
                                type="button"
                                onClick={onClose}
                            >
                                {translate("common.close")}
                            </CloseButton>
                        </Flex>
                    </RightContent>
                ) : null}
            </ContentWrapper>
        </Modal>
    );
};
